import { Component, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { NewCatalogProductListItem, Product } from 'src/typings';
import { environment } from 'src/environments/environment';
import { NotificationService } from '../../services/notification.service';
import { extractApiErrorMessage } from '../../shared/utils/extract-api-error-message.util';
import { NewCatalogService } from '../../new-catalog/new-catalog.service';
import { ProductService } from '../product.service';

@Component({
  selector: 'app-analogue-manual-picker-dialog',
  templateUrl: './analogue-manual-picker-dialog.component.html',
  styleUrls: ['./analogue-manual-picker-dialog.component.scss']
})
export class AnalogueManualPickerDialogComponent {
  readonly searchControl = new FormControl('', { nonNullable: true });

  readonly imagePreviewSizePx = 260;

  readonly imagePreviewOffsetPx = 20;

  loading = false;

  addingProductId?: number;

  createFormOpened = false;

  errorMessage = '';

  results: NewCatalogProductListItem[] = [];

  hoveredImageUrl: string | null = null;

  hoveredImageTitle = '';

  hoveredImagePreviewLeft = 0;

  hoveredImagePreviewTop = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public readonly data: {
      sourceProductId: number;
      excludeProductIds: number[];
    },
    private readonly dialogRef: MatDialogRef<AnalogueManualPickerDialogComponent>,
    private readonly newCatalogService: NewCatalogService,
    private readonly productService: ProductService,
    private readonly notificationService: NotificationService
  ) {}

  getProductImageUrl(item: NewCatalogProductListItem): string | null {
    const imageUrl = String(item.imageUrl || '').trim();
    if (/^(https?:|data:image\/)/i.test(imageUrl)) {
      return imageUrl;
    }

    const imageName = String(item.imageName || '').trim();
    if (/^(https?:|data:image\/)/i.test(imageName)) {
      return imageName;
    }

    if (imageName && this.canResolveImageNameThroughImageEndpoint(imageName)) {
      return `${environment.BACKEND}/image/${imageName}`;
    }

    const fallbackImageUrl = String(item.fallbackImageUrl || '').trim();
    if (/^(https?:|data:image\/)/i.test(fallbackImageUrl)) {
      return fallbackImageUrl;
    }

    return imageName ? `${environment.BACKEND}/image/${imageName}` : null;
  }

  private isRemoteImageReferenceName(imageName?: string | null): boolean {
    return String(imageName || '').startsWith('supplier-remote-');
  }

  private canResolveImageNameThroughImageEndpoint(imageName?: string | null): boolean {
    const normalizedName = String(imageName || '').trim();
    return (
      Boolean(normalizedName) &&
      !/^(https?:|data:image\/)/i.test(normalizedName) &&
      !this.isRemoteImageReferenceName(normalizedName) &&
      !normalizedName.includes('/')
    );
  }

  showImagePreview(event: MouseEvent, imageUrl: string | null, title?: string | null): void {
    if (!imageUrl) return;

    this.hoveredImageUrl = imageUrl;
    this.hoveredImageTitle = title || '';
    this.updateImagePreviewPosition(event);
  }

  moveImagePreview(event: MouseEvent): void {
    if (!this.hoveredImageUrl) return;
    this.updateImagePreviewPosition(event);
  }

  hideImagePreview(): void {
    this.hoveredImageUrl = null;
    this.hoveredImageTitle = '';
  }

  getBrandLogoUrl(item: NewCatalogProductListItem): string | null {
    return item.brandLogo ? `${environment.BACKEND}/image/${item.brandLogo}` : null;
  }

  getBrandRatingRange(item: NewCatalogProductListItem) {
    const rating = Number(item.brandRating ?? 0);
    if (!Number.isFinite(rating) || rating <= 0) {
      return [];
    }

    return Array.from({ length: Math.max(0, Math.min(5, rating)) }, (_, index) => index + 1);
  }

  async submitSearch(event?: Event) {
    event?.preventDefault();
    const query = this.searchControl.value.trim();
    if (!query) {
      this.results = [];
      this.errorMessage = '';
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    try {
      const response = await firstValueFrom(this.newCatalogService.searchProducts(query, 25, 0));
      const excluded = new Set(this.data.excludeProductIds || []);
      this.results = (response.items || []).filter((item) => !excluded.has(item.id));
      if (!this.results.length) {
        this.errorMessage = 'Подходящие товары не найдены.';
      }
    } catch (error) {
      this.errorMessage = extractApiErrorMessage(error, 'Не удалось выполнить поиск.');
    } finally {
      this.loading = false;
    }
  }

  async add(item: NewCatalogProductListItem) {
    await this.addById(item.id, 'Ручной аналог добавлен.');
  }

  toggleCreateForm(): void {
    this.createFormOpened = !this.createFormOpened;
  }

  async handleCreatedProduct(product: Product): Promise<void> {
    await this.addById(product.id, 'Товар создан и добавлен как ручной аналог.');
  }

  private async addById(productId: number, successMessage: string): Promise<void> {
    this.addingProductId = productId;
    try {
      await firstValueFrom(
        this.productService.addManualAnalogueV2({
          source_product_id: this.data.sourceProductId,
          target_product_id: productId
        })
      );
      this.notificationService.notice(successMessage);
      this.dialogRef.close(true);
    } catch (error) {
      this.notificationService.showError(
        extractApiErrorMessage(error, 'Не удалось добавить ручной аналог.')
      );
    } finally {
      this.addingProductId = undefined;
    }
  }

  private updateImagePreviewPosition(event: MouseEvent): void {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const previewSize = this.imagePreviewSizePx;
    const offset = this.imagePreviewOffsetPx;

    let nextLeft = event.clientX + offset;
    let nextTop = event.clientY + offset;

    if (nextLeft + previewSize > viewportWidth - 12) {
      nextLeft = Math.max(12, event.clientX - previewSize - offset);
    }

    if (nextTop + previewSize > viewportHeight - 12) {
      nextTop = Math.max(12, event.clientY - previewSize - offset);
    }

    this.hoveredImagePreviewLeft = nextLeft;
    this.hoveredImagePreviewTop = nextTop;
  }
}
